<script lang="ts" setup>
import { onBeforeMount, ref, watch } from "vue";
import { Checkbox, Popup, showLoadingToast } from "vant";
import { Story, StoryBook } from "@/types/entity";
import { useStory } from "@/stores/story";
import StoryListItem from "@/components/mobile/story/story-chapter/StoryListItem.vue";
import { storeToRefs } from "pinia";
import { useStoryBook } from "@/stores/story-book";
import StoryBookListItem from "@/components/mobile/story/story-book/StoryBookListItem.vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import i18n from "@/locales";
// Props & Emit
const props = withDefaults(
  defineProps<{
    show: boolean;
  }>(),
  {
    show: false,
  }
);

const emit = defineEmits<{
  (e: "update:show", show: boolean): void;
}>();

// State
const storyStore = useStory();
const storyBookStore = useStoryBook();
const { storyWithoutBookList } = storeToRefs(storyStore);
const { allStoryBookList } = storeToRefs(storyBookStore);

const selectedStoryBook = ref<StoryBook | null>(null);
const selectedStories = ref<Story[]>([]);
const loading = ref(false);

// Lifecycle
const prepareData = async () => {
  loading.value = true;
  try {
    await storyStore.getOrFetchStoryWithoutBookList();
    await storyBookStore.getStoryBookList();
  } finally {
    loading.value = false;
  }
};
onBeforeMount(prepareData);

watch(
  () => props.show,
  (value) => {
    if (value) {
      selectedStories.value = [];
      selectedStoryBook.value = null;
      prepareData();
    }
  }
);

// EventHandler
const onSelect = (story: Story) => {
  if (selectedStories.value.find((item) => item.id === story.id)) {
    selectedStories.value = selectedStories.value.filter(
      (item) => item.id !== story.id
    );
  } else {
    selectedStories.value.push(story);
  }
};

const onConfirm = async () => {
  const loadingToast = showLoadingToast({
    message: i18n.global.t("Message.chapterIntoStory"),
  });
  try {
    await storyStore.changeStoryBookId(
      selectedStoryBook.value!.id,
      selectedStories.value.map((item) => item.id)
    );
    emit("update:show", false);
  } finally {
    loadingToast.close();
  }
};
</script>
<template>
  <popup
    :show="show"
    class="h-[80%] overflow-hidden"
    position="bottom"
    round
    teleport="body"
    @update:show="emit('update:show', $event)"
  >
    <div
      v-if="!selectedStoryBook"
      class="h-full w-full overflow-hidden grid grid-rows-[auto_1fr]"
    >
      <div class="navbar h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button
            class="btn btn-sm rounded"
            @click="emit('update:show', false)"
          >
            {{ $t("Buttons.cancel") }}
          </button>
        </div>
        <!-- 中 -->
        <div class="navbar-center">
          <div class="font-bold">{{ $t("Buttons.selectAddToStory") }}</div>
        </div>
        <!-- 右 -->
        <div class="navbar-end"></div>
      </div>
      <div class="w-full overflow-y-auto flex flex-col relative">
        <div v-show="loading" class="h-40 flex items-center justify-center">
          <span class="loading"></span>
        </div>
        <div
          v-show="!loading && allStoryBookList.length === 0"
          class="h-40 flex flex-col items-center justify-center"
        >
          <div class="text-2xl font-bold text-base-content/50">
            {{ $t("Message.noStory") }}
          </div>
          <div class="text- text-base-content/50">
            {{ $t("Message.createAStoryFirst") }}
          </div>
        </div>
        <div
          v-show="!loading && allStoryBookList.length > 0"
          class="p-2 pt-0 w-full overflow-x-hidden"
        >
          <div
            v-for="item in allStoryBookList"
            :key="item.id"
            class="flex gap-2 items-center border border-transparent"
            @click="selectedStoryBook = item"
          >
            <story-book-list-item :story-book="item" class="px-2 text-sm" />
          </div>
        </div>
      </div>
    </div>
    <div v-else class="h-full w-full overflow-hidden grid grid-rows-[auto_1fr]">
      <div class="navbar h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button
            class="btn btn-ghost btn-sm rounded"
            @click="
              selectedStoryBook = null;
              selectedStories = [];
            "
          >
            <light-arrow-icon />
          </button>
        </div>
        <!-- 中 -->
        <div class="navbar-center">
          <div class="font-bold">{{ $t("story.selectChapter") }}</div>
        </div>
        <!-- 右 -->
        <div class="navbar-end">
          <button
            :disabled="selectedStories.length === 0"
            class="btn btn-primary btn-sm rounded"
            @click="onConfirm"
          >
            {{ $t("Buttons.confirm") }}
          </button>
        </div>
      </div>
      <div class="w-full overflow-y-auto flex flex-col relative">
        <div v-show="loading" class="h-40 flex items-center justify-center">
          <span class="loading"></span>
        </div>
        <div
          v-show="!loading && storyWithoutBookList.length === 0"
          class="h-40 flex flex-col items-center justify-center"
        >
          <div class="text-2xl font-bold text-base-content/50">
            {{ $t("story.noChapterAvailableYet") }}
          </div>
        </div>
        <div
          v-show="!loading && storyWithoutBookList.length > 0"
          class="p-2 pt-0 w-full overflow-x-hidden"
        >
          <div
            v-for="item in storyWithoutBookList"
            :key="item.id"
            :class="{
              'border-primary border rounded': selectedStories?.find(
                (story) => story.id === item.id
              ),
              'border border-transparent ': !selectedStories?.find(
                (story) => story.id === item.id
              ),
            }"
            class="flex gap-2 items-center"
            @click="onSelect(item)"
          >
            <Checkbox
              :model-value="
                selectedStories?.find((story) => story.id === item.id)
              "
              class="shrink-0 pl-2"
            />
            <story-list-item :story="item" class="px-2 text-sm" />
          </div>
        </div>
      </div>
    </div>
  </popup>
</template>
